import React, { useState, useEffect } from "react";
import { Dialog } from "@mui/material";
import ApprovalProgress from "../../components/study/course/ApprovalProgress";
import {
  DiagnosticTestContentDialog,
  Section,
} from "../../components/diagnosticTestV2";
import Loader from "../../components/Loader";
import callApi from "../../utils/api";
import { IR_LEVEL_MAPPING } from "../../store/quiz/quizBaseApi";
import { getById, getFirstCourseAtLevel } from "../../store/course/courseBaseApi";
import { updateProfile as updateProfileApi } from "../../store/profile/profileBaseAPi";
import { useStudy } from "../study/hooks";
import { getLessonPlan } from "../../store/course/courseBaseApi";
import { useQuiz } from "../quiz/hooks";
import { APPLICATION_NAME, json_files, nowISO } from "../../utils";
import testMappingJson from "../../assets/data/diagnostic-test-level-1.json";
import level2Json from "../../assets/data/diagnostic-test-level-2.json";
import Welcome from "./Welcome";
import CourseAssigned from "../../components/study/course/CourseAssigned";
import MicPermission from "../../components/MicPermission";
import { useIRGrade } from "../irGrading/hooks";
import { useDiagnosticTest } from "./hooks";
import { useCourse } from "../course/hooks";
import { useDispatch } from "react-redux";
import { sessionTimingAction } from "../../store/sessionTiming/sessionTimingSlice";

const MAX_SECTIONS = 1;
const defaultSelectedMap = testMappingJson[1];

export const DiagnosticTestV2 = ({ testDetail }) => {
  const {
    setIRLevelMapping,
    profile,
    irLevelMapping,
    updateProfile,
    assignCourse,
  } = useStudy();
  const { gradingResult, overallPercentage, updateCurrentCourseDetails } =
    useQuiz();
  const { loadGradeMap, gradingList } = useIRGrade();
  const { setReadingScore } = useDiagnosticTest();
  const { current: currentCourse } = useCourse();
  const dispatch = useDispatch();
  const [selectedTest, setSelectedTest] = useState(
    testDetail?.diagnosticOne || [],
  );
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [totalSections, setTotalSections] = useState(0);
  const [sections, setSections] = useState([]);
  const [section, setSection] = useState({});
  const [diagnosticTest, setDiagnosticTest] = useState({});
  const [completed, setCompleted] = useState(false);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(false);
  // True when the backend returns noCourseForStudentType=true for the target
  // irLevel — admin hasn't authored a course of the student's URBAN/RURAL
  // type at this level. We show a clear "no course available" message
  // instead of assigning a cross-type course.
  const [noCourseForType, setNoCourseForType] = useState(false);

  // Debug: watch for completed changes so we can trace why CourseAssigned may not render
  React.useEffect(() => {
    console.log("DiagnosticTestV2: completed changed ->", completed);
  }, [completed]);
  const [showTestDetailScreen, setShowTestDetailScreen] = useState(false);
  const [showTestContentDialog, setShowTestContentDialog] = useState(false);
  const [requestPermission, setRequestPermission] = useState(false);
  const [permissionGiven, setPermissionGiven] = useState(false);

  const onSectionCompletion = () => {
    if (currentSectionIndex < totalSections - 1) {
      setCurrentSectionIndex(currentSectionIndex + 1);
      setSection(sections[currentSectionIndex + 1]);
    } else {
      handleNextAssessment();
    }
  };

  useEffect(() => {
    loadGradeMap();
  }, []);
  const getCourseMappings = async () =>
    await callApi().asyncFetchContent(json_files.irLevels.url);

  const assignCourseByLevel = async (level) => {
    // Use the backend /level-course endpoint so the student's
    // geographical_type (URBAN/RURAL) is applied. The old code did
    // `subLevels.at(0).irCourseId` which always returned the first sub-level
    // regardless of type — RURAL students got URBAN courses.
    console.log("[Diagnostic→Course] assignCourseByLevel called", {
      level,
      studentId: profile?.id,
      studentName: profile?.userName || profile?.firstName,
      geographicalType: profile?.geographicalType,
    });
    if (!profile?.id || !level) {
      console.warn("[Diagnostic→Course] missing studentId or level — skipping");
      return;
    }
    try {
      const { data } = await getFirstCourseAtLevel({
        studentId: profile.id,
        level,
      });
      console.log("[Diagnostic→Course] backend response", {
        level,
        response: data,
        noCourseForStudentType: data?.noCourseForStudentType,
        assignedCourseId: data?.nextLevel?.irCourseId,
        assignedLevel: data?.nextLevel?.level,
      });
      if (data?.noCourseForStudentType) {
        console.warn(
          "[Diagnostic→Course] NO COURSE for this student type at level",
          level,
          "— admin must author a",
          (profile.geographicalType || "URBAN").toUpperCase(),
          "course at this level in /course-mapping.",
        );
        setNoCourseForType(true);
        setCompleted(true);
        return;
      }
      const _courseId = data?.nextLevel?.irCourseId;
      if (!_courseId) {
        console.warn(
          "[Diagnostic→Course] backend returned no courseId and no flag — unexpected",
          data,
        );
        return;
      }
      console.log(
        "[Diagnostic→Course] ✅ assigning courseId",
        _courseId,
        "to student",
        profile.id,
      );
      await loadAndAssignCourse(_courseId);
      console.log(
        "[Diagnostic→Course] loadAndAssignCourse completed for",
        _courseId,
      );
      setCompleted(true);
    } catch (e) {
      console.error(
        "[Diagnostic→Course] ❌ Failed to resolve first course at level",
        level,
        e,
      );
    }
  };

  const handleNextAssessment = async () => {
    if (
      selectedTest.nextLevelAssessments &&
      selectedTest.nextLevelAssessments.length
    ) {
      // let percentage = (Math.round(gradingResult.scored / gradingResult.total) * 100);
      let { nextLevelAssessments } = selectedTest;
      // let {percentage} = gradingResult;
      let level2 = nextLevelAssessments?.find(
        (e) =>
          Number(overallPercentage) >= Number(e.minPercentage) &&
          Number(overallPercentage) <= Number(e.maxPercentage),
      );
      // console.log(overallPercentage, level2)
      let courseIdNext;
      // console.log('lvel2', level2?.irLevel, nextLevelAssessments);
      if (level2?.irLevel) {
        return await onDiagnosticTestComplete(level2.irLevel);
      } else {
        courseIdNext = level2?.courseId;
        // console.log('ccourse', courseIdNext);
      }

      if (courseIdNext) {
        // console.log('ccourse', courseIdNext);
        let data = testDetail?.diagnosticTwo.find(
          (item) => item.courseId == courseIdNext,
        );
        if (data) {
          setSelectedTest(data);
          return;
        }
      }

      console.error("something wrong with the level assignments");
    } else {
      onDiagnosticTestComplete();
    }
  };

  const loadCourse = async (id) => {
    let { data } = await getById({ id });
    return data;
  };

  const loadAndAssignCourse = async (courseId) => {
    console.log("loadAndAssignCourse: starting for courseId ->", courseId);
    let courseData = await loadCourse(courseId);
    console.log("loadAndAssignCourse: courseData loaded ->", courseData);

    setCourse(courseData);

    let now = nowISO();
    let _course = {
      courseId: courseData.id,
      courseName: courseData.courseName,
      courseCode: courseData.code,
      dateEnrolled: now,
      assignedBy: profile.id,
      assignedDate: now,
      assignedMethod: "",
      currentStatus: "ASSIGNED",
    };

    try {
      // await assignCourse so we can log its resolution/failure
      const resp = await assignCourse({
        body: {
          application: APPLICATION_NAME,
          assigneeRole: "student",
          assignedUserId: profile.id,
          courses: [_course],
        },
      });
      console.log("loadAndAssignCourse: assignCourse response ->", resp);
    } catch (err) {
      console.error("loadAndAssignCourse: assignCourse failed ->", err);
    }
  };

  const onDiagnosticTestComplete = async (level) => {
    console.log("onDiagnosticTestComplete: start, incoming level ->", level);
    setLoading(true);

    // Stop the diagnostic test session timer — the student now moves to the
    // assigned course welcome screen where no session is active.
    dispatch(sessionTimingAction.clearSessionTime());

    let { levelAssignment } = irLevelMapping;
    if (levelAssignment && levelAssignment.length) {
      let mapping = levelAssignment.find((e) => e.grade == gradingResult.grade);

      //  If mapping found, get the course id.
      if (!level && mapping && mapping.level) {
        level = mapping.level;
      }
    }

    console.log("onDiagnosticTestComplete: resolved level ->", level);

    if (level) {
      console.log(
        "onDiagnosticTestComplete: calling assignCourseByLevel with ->",
        level,
      );
      await assignCourseByLevel(level);
      console.log(
        "onDiagnosticTestComplete: assignCourseByLevel finished for ->",
        level,
      );

      try {
        // Direct API call (not the Redux dispatch) so we genuinely await the
        // PUT before unmounting. The dispatched-saga version returned
        // immediately and the in-flight request could die when the user
        // navigated away — which was leaving gradeLevel="" in the DB and
        // re-triggering the diagnostic on next login.
        const resp = await updateProfileApi({
          body: { gradeLevel: level },
          role: "students",
          id: profile.id,
        });
        console.log(
          "onDiagnosticTestComplete: PUT /profile/ir succeeded for gradeLevel ->",
          level,
          "response:",
          resp,
        );
        // Update redux too so other components on the same session see the
        // fresh gradeLevel without a hard reload.
        updateProfile({
          body: { gradeLevel: level },
          role: "students",
          id: profile.id,
        });
      } catch (err) {
        console.error("onDiagnosticTestComplete: PUT /profile/ir failed ->", err);
      }
    } else {
      console.log(
        "onDiagnosticTestComplete: no level determined, skipping assignment/updateProfile",
      );
    }

    setLoading(false);
    console.log("onDiagnosticTestComplete: finished, loading set to false");
  };

  const loadData = async () => {
    let api = callApi(),
      grade = profile.grade,
      lessonPlan = { sections: [] };
    let [mappings, plan] = await Promise.all([
      api.asyncFetchContent(IR_LEVEL_MAPPING),
      getLessonPlan({ id: selectedTest.courseId }),
    ]);
    if (plan && plan.data && plan.data.lessonPlan) {
      lessonPlan = plan.data.lessonPlan;
    }
    // console.log('mmm', mappings);
    let detail;
    //  mappings.find(e => e?.gradeId == grade) || mappings.find(e => e.irLevels.includes(grade));
    if (detail) {
      setIRLevelMapping(detail);
      // let url = detail.diagnosticAssessmentUrl;
      // return await api.asyncFetchContent(url);
    }

    // This will only update the state
    updateCurrentCourseDetails({ courseId: selectedTest.courseId });

    return {
      ...lessonPlan,
      sections: lessonPlan.sections.splice(0, MAX_SECTIONS),
    };
  };

  const handleOnStartTest = () => {
    setShowTestContentDialog(false);
    setShowTestDetailScreen(true);

    // Only start a new session timer if one isn't already running.
    // Diagnostic test sessions are tracked only in localStorage — not in the
    // backend — because the diagnostic course isn't in ir_course_assignments
    // yet, so the backend PUT would fail with "CourseAssignments not exist".
    const existingEndTime = localStorage.getItem("sessionEndTime");
    const hasActiveSession = existingEndTime && Number(existingEndTime) > Date.now();
    if (!hasActiveSession) {
      const sessionMinutes = Number(currentCourse?.duration?.[0]?.sessionTime);
      if (sessionMinutes > 0) {
        dispatch(sessionTimingAction.setSessionTime(sessionMinutes * 60));
        dispatch(sessionTimingAction.startSessionTimer());
      }
    }
  };

  useEffect(() => {
    if (!profile?.id) return;

    loadData().then((_data) => {
      if (!_data || !Object.keys(_data).length) return;
      let { sections: _sections, ...diagnosticTestMeta } = _data;
      if (_sections?.length) {
        setTotalSections(_sections.length);
        setSections(_sections);
        setSection(_sections[currentSectionIndex]);
      }

      setDiagnosticTest(diagnosticTestMeta);
      setReadingScore(0);
    });
  }, [profile, selectedTest]);

  if (!sections.length) {
    return <Loader />;
  }

  if (loading) {
    return <Loader message={"Please wait, course assignment is in progress"} />;
  }

  if (completed && noCourseForType) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          padding: "2rem",
          textAlign: "center",
        }}
      >
        <h2 style={{ color: "var(--mui-palette-primary-blue, #2e3b6e)", marginBottom: "1rem" }}>
          No course available for this level yet
        </h2>
        <p style={{ color: "#555", maxWidth: 520, marginBottom: "1.5rem" }}>
          Your next course is still being prepared for{" "}
          {(profile?.geographicalType || "Urban").toString()} students. Please
          check back soon.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            background: "linear-gradient(180deg, #ffc700 0%, #ff8a00 100%)",
            color: "#fff",
            fontWeight: 700,
            border: "none",
            borderRadius: 999,
            padding: "10px 28px",
            fontSize: "0.95em",
            cursor: "pointer",
            fontFamily: '"Quicksand", sans-serif',
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          Continue →
        </button>
      </div>
    );
  }

  if (completed) {
    return (
      <CourseAssigned
        course={course}
        onStartCourse={() => window.location?.reload()}
      />
    );
  }

  return (
    <React.Fragment>
      {showTestDetailScreen ? (
        <React.Fragment>
          <Section
            section={section}
            currentSection={currentSectionIndex}
            totalSections={totalSections}
            meta={diagnosticTest}
            mapping={selectedTest}
            onDiagnosticTestComplete={onDiagnosticTestComplete}
            onSectionCompletion={onSectionCompletion}
          />
        </React.Fragment>
      ) : (
        <Welcome
          student={profile}
          selectedTest={selectedTest}
          onClickCta={() => setShowTestContentDialog(true)}
        />
      )}
      <MicPermission
        context="Diagnostic Test"
        open={requestPermission}
        onClose={() => setRequestPermission(false)}
        onSuccess={() => setPermissionGiven(true)}
      />
      <DiagnosticTestContentDialog
        open={showTestContentDialog}
        onClose={() => setShowTestContentDialog(false)}
        onRequestPermission={() => setRequestPermission(true)}
        micPermission={permissionGiven}
        onStartTest={handleOnStartTest}
      />
    </React.Fragment>
  );
};
